"use client";

import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check, Minus } from "lucide-react";
import { cn } from "@/utils/utils";
import { styleDrivers } from "@/utils/componentStyle";
import { Label } from "./label";

export interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  /** Label text for the checkbox */
  label?: string;
  /** Description text shown below the label */
  description?: string;
  /** Error message */
  error?: string;
  /** Whether the checkbox shows indeterminate state */
  indeterminate?: boolean;
}

/**
 * Checkbox provides a styled checkbox input with optional label and description.
 * Built on Radix UI Checkbox for full accessibility support.
 */
const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, label, description, error, indeterminate, id, ...props }, ref) => {
  const generatedId = React.useId();
  const checkboxId = id || generatedId;
  const descriptionId = description ? `${checkboxId}-description` : undefined;
  const errorId = error ? `${checkboxId}-error` : undefined;

  const checkboxElement = (
    <CheckboxPrimitive.Root
      ref={ref}
      id={checkboxId}
      className={cn(
        styleDrivers.checkbox.root,
        error && "border-destructive",
        className
      )}
      checked={indeterminate ? "indeterminate" : props.checked}
      aria-describedby={
        [descriptionId, errorId].filter(Boolean).join(" ") || undefined
      }
      aria-invalid={!!error}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        className={styleDrivers.checkbox.indicator}
      >
        {indeterminate ? (
          <Minus className="icon-xs" />
        ) : (
          <Check className="icon-xs" />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );

  if (!label && !description) {
    return checkboxElement;
  }

  return (
    <div className="flex items-start gap-3">
      {checkboxElement}
      <div className="grid gap-1 leading-none">
        {label && (
          <Label
            htmlFor={checkboxId}
            className={styleDrivers.checkbox.label}
          >
            {label}
          </Label>
        )}
        {description && (
          <p
            id={descriptionId}
            className={styleDrivers.checkbox.description}
          >
            {description}
          </p>
        )}
        {error && (
          <p
            id={errorId}
            role="alert"
            className={styleDrivers.checkbox.error}
          >
            {error}
          </p>
        )}
      </div>
    </div>
  );
});

Checkbox.displayName = "Checkbox";

/**
 * CheckboxGroup provides a container for multiple related checkboxes.
 */
export interface CheckboxGroupProps extends React.HTMLAttributes<HTMLFieldSetElement> {
  /** Label for the group */
  legend: string;
  /** Description for the group */
  description?: string;
  /** Error message for the group */
  error?: string;
  /** Whether to visually hide the legend */
  hideLegend?: boolean;
  /** Orientation of the checkboxes */
  orientation?: "vertical" | "horizontal";
  children: React.ReactNode;
}

const CheckboxGroup = React.forwardRef<HTMLFieldSetElement, CheckboxGroupProps>(
  (
    {
      className,
      legend,
      description,
      error,
      hideLegend = false,
      orientation = "vertical",
      children,
      ...props
    },
    ref
  ) => {
    return (
      <fieldset
        ref={ref}
        className={cn("space-y-3", className)}
        aria-invalid={!!error}
        {...props}
      >
        <legend
          className={cn(
            "text-sm font-medium text-foreground",
            hideLegend && "sr-only"
          )}
        >
          {legend}
        </legend>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
        <div
          className={cn(
            orientation === "vertical" ? "space-y-3" : "flex flex-wrap gap-6"
          )}
        >
          {children}
        </div>
        {error && (
          <p role="alert" className="text-xs text-destructive">
            {error}
          </p>
        )}
      </fieldset>
    );
  }
);

CheckboxGroup.displayName = "CheckboxGroup";

export { Checkbox, CheckboxGroup };
export default Checkbox;
